import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
  Alert,
  Badge,
} from 'reactstrap';
import { useDropzone } from 'react-dropzone';
import { contentService, categoryService } from '../services';
import { Category } from '../types';
import BreadCrumb from '../components/Common/BreadCrumb';
import ModuleItemCard, { ModuleFormData } from '../components/ModuleItemCard';
import IntroductionItemCard, { IntroductionFormData } from '../components/IntroductionItemCard';
import SingleContentMediaCard from '../components/SingleContentMediaCard';
import { toast } from 'react-toastify';
import { parseSrt } from '../utils/srtParser';

const AddEditContentPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const selectedCategoryId = location.state?.selectedCategoryId;
  const returnCategory = location.state?.selectedCategory as Category | undefined;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [contentMode, setContentMode] = useState<'single' | 'modules'>('single');
  
  const [formData, setFormData] = useState({
    title: '',
    shortDesc: '',
    description: '',
    categories: [] as string[],
    status: 'published' as 'draft' | 'published' | 'archived',
    transcript: '',
    transcriptTimestamps: [] as number[],
  });
  
  const [file, setFile] = useState<File | null>(null);
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string>('');
  const [transcriptFileName, setTranscriptFileName] = useState<string | null>(null);
  const [existingFileUrl, setExistingFileUrl] = useState<string>('');
  const [existingFileType, setExistingFileType] = useState<'audio' | 'video' | ''>('');
  const [singleMediaStatus, setSingleMediaStatus] = useState<string | undefined>();
  const [singleMediaErrorMessage, setSingleMediaErrorMessage] = useState<string | undefined>();
  const [fileUploadStatus, setFileUploadStatus] = useState<'idle' | 'uploading' | 'uploaded' | 'error'>('idle');
  const [fileUploadProgress, setFileUploadProgress] = useState(0);
  const [fileUploadError, setFileUploadError] = useState<string | undefined>();
  const [thumbnailUploadStatus, setThumbnailUploadStatus] = useState<'idle' | 'uploading' | 'uploaded' | 'error'>('idle');
  const [thumbnailUploadProgress, setThumbnailUploadProgress] = useState(0);
  const [thumbnailUploadError, setThumbnailUploadError] = useState<string | undefined>();
  const [removingTarget, setRemovingTarget] = useState<'file' | 'thumbnail' | null>(null);
  const [modules, setModules] = useState<ModuleFormData[]>([]);
  const [introductions, setIntroductions] = useState<IntroductionFormData[]>([]);
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [parentCategories, setParentCategories] = useState<Category[]>([]);
  const [selectedParentId, setSelectedParentId] = useState<string>('');

  useEffect(() => {
    loadCategories();
    if (id) {
      loadContent();
    }
  }, [id]);

  useEffect(() => {
    // Auto-select parent when selectedCategoryId is provided (from navigation)
    if (selectedCategoryId && allCategories.length > 0) {
      const selectedCat = allCategories.find(cat => cat._id === selectedCategoryId);
      if (selectedCat) {
        const parentId = getParentId(selectedCat);
        if (parentId && parentId !== selectedParentId) {
          setSelectedParentId(parentId);
        }
      }
    }
  }, [selectedCategoryId, allCategories]);

  // When editing content, auto-select parent from content's categories (initial load only)
  useEffect(() => {
    if (id && formData.categories.length > 0 && allCategories.length > 0 && !selectedParentId) {
      const firstCatId = formData.categories[0];
      const firstCat = allCategories.find(cat => cat._id === firstCatId);
      if (firstCat) {
        const parentId = getParentId(firstCat);
        const toSelect = parentId || firstCat._id;
        if (toSelect) {
          setSelectedParentId(toSelect);
        }
      }
    }
  }, [id, formData.categories, allCategories, selectedParentId]);

  const getParentId = (cat: any): string => {
    if (!cat.parentId) return '';
    if (typeof cat.parentId === 'string') return cat.parentId;
    if (typeof cat.parentId === 'object' && cat.parentId !== null && cat.parentId._id) {
      return cat.parentId._id;
    }
    return '';
  };

  const loadCategories = async () => {
    try {
      const response = await categoryService.getAll();
      const categories = Array.isArray(response) ? response : response.data || [];
      
      const parents = categories.filter((cat: any) => !cat.parentId);
      const subcategories = categories.filter((cat: any) => cat.parentId);
      
      setParentCategories(parents);
      setAllCategories(categories);
      
      if (parents.length === 1 && !selectedParentId) {
        setSelectedParentId(parents[0]._id);
      }
    } catch (err) {
      console.error('Failed to load categories');
    }
  };

  const loadContent = async () => {
    setLoading(true);
    try {
      const data = await contentService.getOne(id!);
      
      const categoryIds = (data.categories || []).map((cat: any) => {
        if (typeof cat === 'object' && cat !== null && cat._id) {
          return cat._id;
        }
        return cat;
      });
      
      setFormData({
        title: data.title || '',
        shortDesc: data.shortDesc || '',
        description: data.description || '',
        categories: categoryIds,
        status: data.status || 'published',
        transcript: data.transcript || '',
        transcriptTimestamps: data.transcriptTimestamps || [],
      });
      
      if (data.modules && data.modules.length > 0) {
        setContentMode('modules');
        setExistingFileUrl('');
        setExistingFileType('');
        const introList =
          (data.introductions && data.introductions.length > 0)
            ? data.introductions
            : data.introduction?.fileUrl || data.introduction?.title
              ? [data.introduction]
              : [];
        setIntroductions(
          introList.map((intro: any, idx: number) => ({
            order: intro.order || idx + 1,
            sourceOrder: intro.order || idx + 1,
            title: intro.title || '',
            transcript: intro.transcript || '',
            transcriptTimestamps: intro.transcriptTimestamps || [],
            transcriptFileName: null,
            file: null,
            thumbnail: null,
            thumbnailPreview: intro.thumbnail || '',
            fileUrl: intro.fileUrl || '',
            type: 'video',
            mediaStatus: intro.mediaStatus || undefined,
            mediaErrorMessage: intro.mediaErrorMessage || undefined,
            mediaUploaded: Boolean(intro.fileUrl || intro.thumbnail),
            fileUploadStatus: 'idle' as const,
            thumbnailUploadStatus: 'idle' as const,
          })),
        );
        setModules(data.modules.map((m: any, idx: number) => ({
          day: m.day || idx + 1,
          serverModuleId: m._id ? String(m._id) : undefined,
          title: m.title || '',
          shortDesc: m.shortDesc || '',
          description: m.description || '',
          transcript: m.transcript || '',
          transcriptTimestamps: m.transcriptTimestamps || [],
          transcriptFileName: null,
          file: null,
          thumbnail: null,
          thumbnailPreview: m.thumbnail || '',
          fileUrl: m.fileUrl || '',
          type: m.type || 'audio',
          mediaStatus: m.mediaStatus || undefined,
          mediaErrorMessage: m.mediaErrorMessage || undefined,
          mediaUploaded: Boolean(m.fileUrl || m.thumbnail),
          fileUploadStatus: 'idle' as const,
          thumbnailUploadStatus: 'idle' as const,
          questions: (m.questions || []).map((q: any, qi: number) => ({
            order: q.order ?? qi + 1,
            prompt: q.prompt || '',
            instruction: q.instruction || '',
          })),
          reflection: {
            title: m.reflection?.title || '',
            description: m.reflection?.description || '',
            bulletPoints: m.reflection?.bulletPoints || [],
          },
        })));
      } else {
        setContentMode('single');
        setThumbnailPreview(data.thumbnail || '');
        setExistingFileUrl(data.fileUrl || '');
        setExistingFileType(data.type || '');
        setSingleMediaStatus(data.mediaStatus || undefined);
        setSingleMediaErrorMessage(data.mediaErrorMessage || undefined);
        setFileUploadStatus('idle');
        setFileUploadProgress(0);
        setFileUploadError(undefined);
        setThumbnailUploadStatus('idle');
        setThumbnailUploadProgress(0);
        setThumbnailUploadError(undefined);
        setRemovingTarget(null);
      }
      
      setFile(null);
      setThumbnail(null);
      setTranscriptFileName(null);
    } catch (err) {
      setError('Failed to load content');
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const addModule = () => {
    setModules(prev => [...prev, {
      day: prev.length + 1,
      title: '',
      shortDesc: '',
      description: '',
      transcript: '',
      transcriptTimestamps: [],
      transcriptFileName: null,
      file: null,
      thumbnail: null,
      thumbnailPreview: undefined,
      type: 'audio',
      questions: [],
      reflection: { title: '', description: '', bulletPoints: [] },
    }]);
  };

  const removeModule = (index: number) => {
    setModules(prev => prev.filter((_, i) => i !== index));
  };

  const updateModule = (index: number, field: keyof ModuleFormData, value: any) => {
    setModules(prev => {
      const updated = [...prev];
      const next = { ...updated[index], [field]: value };
      if (field === 'title' && typeof value === 'string' && value.trim()) {
        if (next.fileUploadStatus === 'error') next.fileUploadStatus = 'idle';
        if (next.thumbnailUploadStatus === 'error') next.thumbnailUploadStatus = 'idle';
        next.fileUploadError = undefined;
        next.thumbnailUploadError = undefined;
      }
      updated[index] = next;
      return updated;
    });
  };

  const handleModuleMediaUpload = async (
    index: number,
    payload: { file?: File; thumbnail?: File },
  ) => {
    if (!id) {
      toast.info('Save content once to enable auto-upload for each day.');
      return;
    }

    const target: 'file' | 'thumbnail' = payload.file
      ? 'file'
      : payload.thumbnail
        ? 'thumbnail'
        : 'file';

    setModules((prev) => {
      const updated = [...prev];
      const current = { ...updated[index] };
      if (target === 'file') {
        current.fileUploadStatus = 'uploading';
        current.fileUploadProgress = 0;
        current.fileUploadError = undefined;
      } else {
        current.thumbnailUploadStatus = 'uploading';
        current.thumbnailUploadProgress = 0;
        current.thumbnailUploadError = undefined;
      }
      updated[index] = current;
      return updated;
    });

    const persistResult = await persistContentMetadata({ type: 'module', index });
    if (!persistResult.ok) {
      setModules((prev) => {
        const updated = [...prev];
        const current = { ...updated[index] };
        if (target === 'file') {
          current.fileUploadStatus = 'error';
          current.fileUploadError = persistResult.message;
        } else {
          current.thumbnailUploadStatus = 'error';
          current.thumbnailUploadError = persistResult.message;
        }
        updated[index] = current;
        return updated;
      });
      toast.error(persistResult.message);
      return;
    }

    const formData = new FormData();
    if (payload.file) formData.append('file', payload.file);
    if (payload.thumbnail) formData.append('thumbnail', payload.thumbnail);

    try {
      const result = await contentService.uploadModuleMedia(id, index, formData, (percent) => {
        setModules((prev) => {
          const updated = [...prev];
          const current = updated[index];
          if (!current) return prev;
          if (target === 'file' && current.fileUploadStatus !== 'uploading') return prev;
          if (target === 'thumbnail' && current.thumbnailUploadStatus !== 'uploading') return prev;
          updated[index] = {
            ...current,
            ...(target === 'file'
              ? { fileUploadProgress: percent }
              : { thumbnailUploadProgress: percent }),
          };
          return updated;
        });
      });

      const serverModule = result.module as Record<string, unknown>;
      setModules((prev) => {
        const updated = [...prev];
        const current = { ...updated[index] };
        if (target === 'file') {
          updated[index] = {
            ...current,
            file: null,
            fileUrl: (serverModule.fileUrl as string) || current.fileUrl,
            type: (serverModule.type as ModuleFormData['type']) || current.type,
            mediaStatus: serverModule.mediaStatus as string | undefined,
            mediaErrorMessage: serverModule.mediaErrorMessage as string | undefined,
            mediaUploaded: true,
            fileUploadStatus: 'uploaded',
            fileUploadProgress: 100,
            fileUploadError: undefined,
          };
        } else {
          updated[index] = {
            ...current,
            thumbnail: null,
            thumbnailPreview: (serverModule.thumbnail as string) || current.thumbnailPreview,
            mediaUploaded: true,
            thumbnailUploadStatus: 'uploaded',
            thumbnailUploadProgress: 100,
            thumbnailUploadError: undefined,
          };
        }
        return updated;
      });
      toast.success(`Day ${index + 1} ${target === 'file' ? 'video' : 'thumbnail'} uploaded`);
    } catch (err: any) {
      const message =
        err.response?.data?.message ||
        (typeof err.response?.data === 'string' ? err.response.data : '') ||
        'Upload failed';
      const friendlyMessage = /not found/i.test(message)
        ? 'Save the content first to add this day, then upload media.'
        : message;
      setModules((prev) => {
        const updated = [...prev];
        const current = { ...updated[index] };
        if (target === 'file') {
          current.fileUploadStatus = 'error';
          current.fileUploadError = friendlyMessage;
        } else {
          current.thumbnailUploadStatus = 'error';
          current.thumbnailUploadError = friendlyMessage;
        }
        updated[index] = current;
        return updated;
      });
    }
  };

  const handleModuleMediaRemove = async (index: number, target: 'file' | 'thumbnail') => {
    const mod = modules[index];
    const hasServerMedia =
      Boolean(id) &&
      (target === 'file'
        ? Boolean(mod.fileUrl)
        : Boolean(mod.thumbnailPreview && !mod.thumbnail));

    if (!hasServerMedia) {
      setModules((prev) => {
        const updated = [...prev];
        if (target === 'file') {
          updated[index] = {
            ...updated[index],
            file: null,
            fileUrl: undefined,
            type: undefined,
            mediaStatus: undefined,
            mediaUploaded: Boolean(updated[index].thumbnailPreview),
            fileUploadStatus: 'idle',
            fileUploadError: undefined,
          };
        } else {
          updated[index] = {
            ...updated[index],
            thumbnail: null,
            thumbnailPreview: undefined,
            mediaUploaded: Boolean(updated[index].fileUrl),
          };
        }
        updated[index].removingTarget = null;
        return updated;
      });
      toast.success(`Day ${index + 1} ${target === 'file' ? 'media' : 'thumbnail'} removed`);
      return;
    }

    setModules((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], removingTarget: target };
      return updated;
    });

    try {
      await contentService.removeModuleMedia(id!, index, target);
      setModules((prev) => {
        const updated = [...prev];
        if (target === 'file') {
          updated[index] = {
            ...updated[index],
            file: null,
            fileUrl: undefined,
            type: undefined,
            mediaStatus: undefined,
            mediaUploaded: Boolean(updated[index].thumbnailPreview),
            fileUploadStatus: 'idle',
            fileUploadError: undefined,
            removingTarget: null,
          };
        } else {
          updated[index] = {
            ...updated[index],
            thumbnail: null,
            thumbnailPreview: undefined,
            mediaUploaded: Boolean(updated[index].fileUrl),
            removingTarget: null,
          };
        }
        return updated;
      });
      toast.success(`Day ${index + 1} ${target === 'file' ? 'media' : 'thumbnail'} removed`);
    } catch (err: any) {
      const message =
        err.response?.data?.message ||
        (typeof err.response?.data === 'string' ? err.response.data : '') ||
        'Remove failed';
      setModules((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], removingTarget: null };
        return updated;
      });
    }
  };

  const handleIntroductionMediaUpload = async (
    index: number,
    payload: { file?: File; thumbnail?: File },
  ) => {
    if (!id) {
      toast.info('Save content once to enable auto-upload for introductions.');
      return;
    }

    const target: 'file' | 'thumbnail' = payload.file
      ? 'file'
      : payload.thumbnail
        ? 'thumbnail'
        : 'file';

    setIntroductions((prev) => {
      const updated = [...prev];
      const current = { ...updated[index] };
      if (target === 'file') {
        current.fileUploadStatus = 'uploading';
        current.fileUploadProgress = 0;
        current.fileUploadError = undefined;
      } else {
        current.thumbnailUploadStatus = 'uploading';
        current.thumbnailUploadProgress = 0;
        current.thumbnailUploadError = undefined;
      }
      updated[index] = current;
      return updated;
    });

    const persistResult = await persistContentMetadata({ type: 'introduction', index });
    if (!persistResult.ok) {
      setIntroductions((prev) => {
        const updated = [...prev];
        const current = { ...updated[index] };
        if (target === 'file') {
          current.fileUploadStatus = 'error';
          current.fileUploadError = persistResult.message;
        } else {
          current.thumbnailUploadStatus = 'error';
          current.thumbnailUploadError = persistResult.message;
        }
        updated[index] = current;
        return updated;
      });
      toast.error(persistResult.message);
      return;
    }

    const formData = new FormData();
    if (payload.file) formData.append('file', payload.file);
    if (payload.thumbnail) formData.append('thumbnail', payload.thumbnail);

    try {
      const result = await contentService.uploadIntroductionMedia(id, index, formData, (percent) => {
        setIntroductions((prev) => {
          const updated = [...prev];
          const current = updated[index];
          if (!current) return prev;
          if (target === 'file' && current.fileUploadStatus !== 'uploading') return prev;
          if (target === 'thumbnail' && current.thumbnailUploadStatus !== 'uploading') return prev;
          updated[index] = {
            ...current,
            ...(target === 'file'
              ? { fileUploadProgress: percent }
              : { thumbnailUploadProgress: percent }),
          };
          return updated;
        });
      });

      const serverIntro = result.introduction as Record<string, unknown>;
      setIntroductions((prev) => {
        const updated = [...prev];
        const current = { ...updated[index] };
        if (target === 'file') {
          updated[index] = {
            ...current,
            file: null,
            fileUrl: (serverIntro.fileUrl as string) || current.fileUrl,
            mediaStatus: serverIntro.mediaStatus as string | undefined,
            mediaErrorMessage: serverIntro.mediaErrorMessage as string | undefined,
            mediaUploaded: true,
            fileUploadStatus: 'uploaded',
            fileUploadProgress: 100,
            fileUploadError: undefined,
          };
        } else {
          updated[index] = {
            ...current,
            thumbnail: null,
            thumbnailPreview: (serverIntro.thumbnail as string) || current.thumbnailPreview,
            mediaUploaded: true,
            thumbnailUploadStatus: 'uploaded',
            thumbnailUploadProgress: 100,
            thumbnailUploadError: undefined,
          };
        }
        return updated;
      });
      toast.success(`Introduction ${index + 1} ${target === 'file' ? 'video' : 'thumbnail'} uploaded`);
    } catch (err: any) {
      const message =
        err.response?.data?.message ||
        (typeof err.response?.data === 'string' ? err.response.data : '') ||
        'Upload failed';
      const friendlyMessage = /not found/i.test(message)
        ? 'Save the content first to add this introduction, then upload media.'
        : message;
      setIntroductions((prev) => {
        const updated = [...prev];
        const current = { ...updated[index] };
        if (target === 'file') {
          current.fileUploadStatus = 'error';
          current.fileUploadError = friendlyMessage;
        } else {
          current.thumbnailUploadStatus = 'error';
          current.thumbnailUploadError = friendlyMessage;
        }
        updated[index] = current;
        return updated;
      });
    }
  };

  const handleIntroductionMediaRemove = async (index: number, target: 'file' | 'thumbnail') => {
    const intro = introductions[index];
    const hasServerMedia =
      Boolean(id) &&
      (target === 'file'
        ? Boolean(intro.fileUrl)
        : Boolean(intro.thumbnailPreview && !intro.thumbnail));

    if (!hasServerMedia) {
      setIntroductions((prev) => {
        const updated = [...prev];
        if (target === 'file') {
          updated[index] = {
            ...updated[index],
            file: null,
            fileUrl: undefined,
            mediaStatus: undefined,
            mediaUploaded: Boolean(updated[index].thumbnailPreview),
            fileUploadStatus: 'idle',
            fileUploadError: undefined,
          };
        } else {
          updated[index] = {
            ...updated[index],
            thumbnail: null,
            thumbnailPreview: undefined,
            mediaUploaded: Boolean(updated[index].fileUrl),
          };
        }
        updated[index].removingTarget = null;
        return updated;
      });
      toast.success(`Introduction ${index + 1} ${target === 'file' ? 'video' : 'thumbnail'} removed`);
      return;
    }

    setIntroductions((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], removingTarget: target };
      return updated;
    });

    try {
      await contentService.removeIntroductionMedia(id!, index, target);
      setIntroductions((prev) => {
        const updated = [...prev];
        if (target === 'file') {
          updated[index] = {
            ...updated[index],
            file: null,
            fileUrl: undefined,
            mediaStatus: undefined,
            mediaUploaded: Boolean(updated[index].thumbnailPreview),
            fileUploadStatus: 'idle',
            fileUploadError: undefined,
            removingTarget: null,
          };
        } else {
          updated[index] = {
            ...updated[index],
            thumbnail: null,
            thumbnailPreview: undefined,
            mediaUploaded: Boolean(updated[index].fileUrl),
            removingTarget: null,
          };
        }
        return updated;
      });
      toast.success(`Introduction ${index + 1} ${target === 'file' ? 'video' : 'thumbnail'} removed`);
    } catch {
      setIntroductions((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], removingTarget: null };
        return updated;
      });
    }
  };

  const handleSingleMediaUpload = async (payload: { file?: File; thumbnail?: File }) => {
    if (!formData.title.trim()) {
      toast.info('Add a content title before uploading media.');
      return;
    }

    if (payload.file) {
      setFile(payload.file);
      setExistingFileType(payload.file.type.startsWith('video/') ? 'video' : 'audio');
    }
    if (payload.thumbnail) {
      setThumbnail(payload.thumbnail);
      setThumbnailPreview(URL.createObjectURL(payload.thumbnail));
    }

    if (!id) {
      toast.info('Save content once to enable auto-upload for media.');
      return;
    }

    const target: 'file' | 'thumbnail' = payload.file
      ? 'file'
      : payload.thumbnail
        ? 'thumbnail'
        : 'file';

    if (target === 'file') {
      setFileUploadStatus('uploading');
      setFileUploadProgress(1);
      setFileUploadError(undefined);
    } else {
      setThumbnailUploadStatus('uploading');
      setThumbnailUploadProgress(1);
      setThumbnailUploadError(undefined);
    }

    const uploadData = new FormData();
    if (payload.file) uploadData.append('file', payload.file);
    if (payload.thumbnail) uploadData.append('thumbnail', payload.thumbnail);

    try {
      const result = await contentService.uploadContentMedia(id, uploadData, (percent) => {
        if (target === 'file') {
          setFileUploadProgress(percent);
        } else {
          setThumbnailUploadProgress(percent);
        }
      });

      const serverContent = result.content as Record<string, unknown>;
      if (target === 'file') {
        setFile(null);
        setExistingFileUrl((serverContent.fileUrl as string) || '');
        setExistingFileType((serverContent.type as 'audio' | 'video') || '');
        setSingleMediaStatus(serverContent.mediaStatus as string | undefined);
        setSingleMediaErrorMessage(serverContent.mediaErrorMessage as string | undefined);
        setFileUploadStatus('uploaded');
        setFileUploadProgress(100);
        setFileUploadError(undefined);
      } else {
        setThumbnail(null);
        setThumbnailPreview((serverContent.thumbnail as string) || '');
        setThumbnailUploadStatus('uploaded');
        setThumbnailUploadProgress(100);
        setThumbnailUploadError(undefined);
      }
      toast.success(`${target === 'file' ? 'Media' : 'Thumbnail'} uploaded`);
    } catch (err: any) {
      const message =
        err.response?.data?.message ||
        (typeof err.response?.data === 'string' ? err.response.data : '') ||
        'Upload failed';
      if (target === 'file') {
        setFileUploadStatus('error');
        setFileUploadError(message);
      } else {
        setThumbnailUploadStatus('error');
        setThumbnailUploadError(message);
      }
      toast.error(message);
    }
  };

  const handleSingleMediaRemove = async (target: 'file' | 'thumbnail') => {
    const hasServerMedia =
      Boolean(id) &&
      (target === 'file'
        ? Boolean(existingFileUrl && !file)
        : Boolean(thumbnailPreview && !thumbnail));

    if (!hasServerMedia) {
      if (target === 'file') {
        setFile(null);
        setExistingFileUrl('');
        setExistingFileType('');
        setSingleMediaStatus(undefined);
        setSingleMediaErrorMessage(undefined);
        setFileUploadStatus('idle');
        setFileUploadError(undefined);
      } else {
        setThumbnail(null);
        setThumbnailPreview('');
        setThumbnailUploadStatus('idle');
        setThumbnailUploadError(undefined);
      }
      setRemovingTarget(null);
      toast.success(`${target === 'file' ? 'Media' : 'Thumbnail'} removed`);
      return;
    }

    setRemovingTarget(target);
    try {
      const result = await contentService.removeContentMedia(id!, target);
      const serverContent = result.content as Record<string, unknown>;
      if (target === 'file') {
        setFile(null);
        setExistingFileUrl('');
        setExistingFileType('');
        setSingleMediaStatus(undefined);
        setSingleMediaErrorMessage(undefined);
        setFileUploadStatus('idle');
        setFileUploadError(undefined);
      } else {
        setThumbnail(null);
        setThumbnailPreview((serverContent.thumbnail as string) || '');
        setThumbnailUploadStatus('idle');
        setThumbnailUploadError(undefined);
      }
      toast.success(`${target === 'file' ? 'Media' : 'Thumbnail'} removed`);
    } catch (err: any) {
      const message =
        err.response?.data?.message ||
        (typeof err.response?.data === 'string' ? err.response.data : '') ||
        'Remove failed';
      toast.error(message);
    } finally {
      setRemovingTarget(null);
    }
  };

  const addIntroduction = () => {
    setIntroductions((prev) => [
      ...prev,
      {
        order: prev.length + 1,
        title: '',
        transcript: '',
        transcriptTimestamps: [],
        transcriptFileName: null,
        file: null,
        thumbnail: null,
        thumbnailPreview: undefined,
        type: 'video',
      },
    ]);
  };

  const removeIntroduction = (index: number) => {
    setIntroductions((prev) => prev.filter((_, i) => i !== index));
  };

  const updateIntroduction = (index: number, field: keyof IntroductionFormData, value: unknown) => {
    setIntroductions((prev) => {
      const updated = [...prev];
      const next = { ...updated[index], [field]: value };
      if (field === 'title' && typeof value === 'string' && value.trim()) {
        if (next.fileUploadStatus === 'error') next.fileUploadStatus = 'idle';
        if (next.thumbnailUploadStatus === 'error') next.thumbnailUploadStatus = 'idle';
        next.fileUploadError = undefined;
        next.thumbnailUploadError = undefined;
      }
      updated[index] = next;
      return updated;
    });
  };

  const handleSrtFile = (srtFile: File) => {
    if (!srtFile || srtFile.size === 0) {
      toast.error('File is empty. Choose an SRT file that contains transcript content.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = (reader.result as string) || '';
        const { transcript, transcriptTimestamps } = parseSrt(text);
        if (!text.trim()) {
          toast.error('File is empty or could not be read.');
          return;
        }
        if (!transcript.trim() && transcriptTimestamps.length === 0) {
          toast.error('No valid SRT cues found. Check the file format (sequence number, time line, then text).');
          return;
        }
        setTranscriptFileName(srtFile.name);
        setFormData((prev) => ({
          ...prev,
          transcript,
          transcriptTimestamps,
        }));
        toast.success(`Transcript imported: ${transcriptTimestamps.length} timestamp(s)`);
      } catch {
        toast.error('Invalid SRT file');
      }
    };
    reader.onerror = () => toast.error('Could not read the file.');
    reader.readAsText(srtFile);
  };

  const { getRootProps: getTranscriptProps, getInputProps: getTranscriptInputProps } = useDropzone({
    accept: {
      'text/plain': ['.srt'],
      'application/x-subrip': ['.srt'],
      'text/x-srt': ['.srt'],
      'application/octet-stream': ['.srt'],
    },
    maxFiles: 1,
    onDrop: (acceptedFiles, rejectedFiles) => {
      if (acceptedFiles.length > 0) {
        handleSrtFile(acceptedFiles[0]);
      } else if (rejectedFiles.length > 0) {
        const file = rejectedFiles[0].file;
        if (file.name.toLowerCase().endsWith('.srt')) {
          handleSrtFile(file);
        } else {
          toast.error('Please use a .srt file only');
        }
      }
    },
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const categoryId = e.target.value;
    const isChecked = e.target.checked;

    setFormData((prev) => ({
      ...prev,
      categories: isChecked
        ? [...prev.categories, categoryId]
        : prev.categories.filter((id) => id !== categoryId),
    }));
  };

  const navigateBackToContent = (refresh = false) => {
    const category =
      returnCategory ||
      (formData.categories[0]
        ? allCategories.find((cat) => cat._id === formData.categories[0])
        : undefined);

    navigate('/dashboard/content', {
      state: {
        ...(refresh && { refresh: true }),
        ...(category && { selectedCategory: category }),
      },
    });
  };

  const validateContentForm = (
    uploadContext?: { type: 'module' | 'introduction'; index: number },
  ): string | null => {
    if (!formData.title.trim()) {
      return uploadContext ? 'Content title is required before uploading media' : 'Title is required';
    }
    if (formData.categories.length === 0) {
      return uploadContext
        ? 'Select at least one category before uploading media'
        : 'Please select at least one category';
    }
    if (contentMode === 'modules') {
      if (uploadContext?.type === 'module') {
        if (!modules[uploadContext.index]?.title?.trim()) {
          return 'Add a title for this day before uploading media';
        }
      } else if (uploadContext?.type === 'introduction') {
        if (!introductions[uploadContext.index]?.title?.trim()) {
          return 'Add a title for this introduction before uploading media';
        }
      } else {
        const invalidModule = modules.find((m) => !m.title?.trim());
        if (invalidModule) {
          return 'Each module must have a title';
        }
        const invalidIntro = introductions.find((i) => !i.title?.trim());
        if (invalidIntro) {
          return 'Each introduction must have a title';
        }
      }
    }
    return null;
  };

  const buildContentFormData = (options?: {
    includeLocalMedia?: boolean;
    relaxMissingTitles?: boolean;
  }) => {
    const includeLocalMedia = options?.includeLocalMedia ?? true;
    const relaxMissingTitles = options?.relaxMissingTitles ?? false;
    const data = new FormData();
    data.append('title', formData.title);
    data.append('shortDesc', formData.shortDesc ?? '');
    data.append('description', formData.description ?? '');
    data.append('status', 'published');
    data.append('transcript', formData.transcript ?? '');
    if (formData.transcriptTimestamps?.length) {
      data.append('transcriptTimestamps', JSON.stringify(formData.transcriptTimestamps));
    }
    data.append('categories', JSON.stringify(formData.categories));

    if (contentMode === 'single') {
      if (includeLocalMedia && file) data.append('file', file);
      if (includeLocalMedia && thumbnail) data.append('thumbnail', thumbnail);
    } else if (contentMode === 'modules' && modules.length > 0) {
      const modulesPayload = modules.map((m, idx) => ({
        day: idx + 1,
        ...(m.serverModuleId ? { serverModuleId: m.serverModuleId } : {}),
        title: m.title?.trim() || (relaxMissingTitles ? `Day ${idx + 1}` : m.title),
        shortDesc: m.shortDesc || '',
        description: m.description || '',
        transcript: m.transcript || '',
        transcriptTimestamps: m.transcriptTimestamps?.length ? m.transcriptTimestamps : undefined,
        type: m.type || 'audio',
        questions: (m.questions || [])
          .filter((q) => q.prompt?.trim())
          .map((q, qi) => ({
            order: qi + 1,
            prompt: q.prompt,
            instruction: q.instruction || undefined,
          })),
        reflection: (() => {
          const r = m.reflection || {};
          const bulletPoints = (r.bulletPoints || []).map((b) => b.trim()).filter(Boolean);
          const title = r.title?.trim() || '';
          const description = r.description?.trim() || '';
          if (!title && !description && bulletPoints.length === 0) return null;
          return {
            ...(title ? { title } : {}),
            ...(description ? { description } : {}),
            ...(bulletPoints.length ? { bulletPoints } : {}),
          };
        })(),
      }));
      data.append('modules', JSON.stringify(modulesPayload));

      if (includeLocalMedia) {
        modules.forEach((m, idx) => {
          if (m.type !== 'question' && m.file) {
            data.append(`module_${idx}`, m.file);
          }
          if (m.type !== 'question' && m.thumbnail) {
            data.append(`moduleThumbnail_${idx}`, m.thumbnail);
          }
        });
      }
    }

    if (contentMode === 'modules' && id) {
      const introductionsPayload = introductions.map((intro, idx) => ({
        order: idx + 1,
        sourceOrder: intro.sourceOrder ?? idx + 1,
        title: intro.title?.trim() || (relaxMissingTitles ? `Introduction ${idx + 1}` : intro.title),
        transcript: intro.transcript || '',
        transcriptTimestamps: intro.transcriptTimestamps?.length ? intro.transcriptTimestamps : undefined,
      }));
      data.append('introductions', JSON.stringify(introductionsPayload));
      if (includeLocalMedia) {
        introductions.forEach((intro, idx) => {
          if (intro.file) data.append(`introduction_${idx}`, intro.file);
          if (intro.thumbnail) data.append(`introductionThumbnail_${idx}`, intro.thumbnail);
        });
      }
    } else if (contentMode === 'modules' && introductions.length > 0) {
      const introductionsPayload = introductions.map((intro, idx) => ({
        order: idx + 1,
        sourceOrder: intro.sourceOrder ?? idx + 1,
        title: intro.title?.trim() || (relaxMissingTitles ? `Introduction ${idx + 1}` : intro.title),
        transcript: intro.transcript || '',
        transcriptTimestamps: intro.transcriptTimestamps?.length ? intro.transcriptTimestamps : undefined,
      }));
      data.append('introductions', JSON.stringify(introductionsPayload));
      if (includeLocalMedia) {
        introductions.forEach((intro, idx) => {
          if (intro.file) data.append(`introduction_${idx}`, intro.file);
          if (intro.thumbnail) data.append(`introductionThumbnail_${idx}`, intro.thumbnail);
        });
      }
    }

    return data;
  };

  /** Sync module/introduction metadata to the server so per-day media uploads have a target slot. */
  const persistContentMetadata = async (
    uploadContext?: { type: 'module' | 'introduction'; index: number },
  ): Promise<{ ok: true } | { ok: false; message: string }> => {
    const validationError = validateContentForm(uploadContext);
    if (validationError) {
      return { ok: false, message: validationError };
    }
    if (!id) {
      return { ok: false, message: 'Save content once to enable media uploads.' };
    }

    try {
      await contentService.update(
        id,
        buildContentFormData({ includeLocalMedia: false, relaxMissingTitles: Boolean(uploadContext) }),
      );
      return { ok: true };
    } catch (err: any) {
      const message =
        err.response?.data?.message ||
        (typeof err.response?.data === 'string' ? err.response.data : '') ||
        'Failed to save content metadata';
      return { ok: false, message };
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateContentForm();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setSaving(true);
    setError('');

    try {
      const data = buildContentFormData({ includeLocalMedia: true });

      if (id) {
        await contentService.update(id, data);
        toast.success('Content updated successfully');
        navigateBackToContent(true);
      } else {
        const created = await contentService.create(data);
        if (contentMode === 'modules') {
          toast.success('Content created. You can now upload media for each day.');
          navigate(`/dashboard/content/edit/${created._id}`, {
            replace: true,
            state: {
              selectedCategoryId,
              selectedCategory: returnCategory,
            },
          });
        } else {
          toast.success('Content created successfully');
          navigateBackToContent(true);
        }
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to save content';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigateBackToContent();
  };

  const getCategoryById = (id: string) => {
    return allCategories.find((c) => c._id === id);
  };

  const filteredSubcategories = selectedParentId
    ? allCategories.filter((cat) => getParentId(cat) === selectedParentId)
    : [];

  const hasActiveMediaUpload =
    fileUploadStatus === 'uploading' ||
    thumbnailUploadStatus === 'uploading' ||
    modules.some(
      (m) => m.fileUploadStatus === 'uploading' || m.thumbnailUploadStatus === 'uploading',
    ) ||
    introductions.some(
      (i) => i.fileUploadStatus === 'uploading' || i.thumbnailUploadStatus === 'uploading',
    );
  const formActionsDisabled = saving || hasActiveMediaUpload;

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2 text-muted">Loading content...</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb 
            title={id ? 'Edit Content' : 'Add Content'} 
            pageTitle="Content Management" 
          />

          {error && (
            <Alert color="danger" toggle={() => setError('')}>
              <i className="ri-error-warning-line me-2"></i>
              {error}
            </Alert>
          )}

          <Form onSubmit={handleSubmit}>
            <Card>
              <CardBody>
                <h5 className="card-title mb-4">
                  <i className="ri-information-line me-2"></i>
                  Basic Information
                </h5>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label htmlFor="title" className="form-label fw-semibold">
                        Content Title <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="text"
                        id="title"
                        name="title"
                        placeholder="Enter content title"
                        value={formData.title}
                        onChange={handleChange}
                        required
                        className="form-control"
                      />
                    </FormGroup>
                  </Col>

                  <Col md={6}>
                    <FormGroup>
                      <Label htmlFor="shortDesc" className="form-label fw-semibold">
                        Short Description
                      </Label>
                      <Input
                        type="text"
                        id="shortDesc"
                        name="shortDesc"
                        placeholder="Brief description of content"
                        value={formData.shortDesc}
                        onChange={handleChange}
                        className="form-control"
                      />
                    </FormGroup>
                  </Col>

                  <Col md={12}>
                    <FormGroup>
                      <Label htmlFor="description" className="form-label fw-semibold">
                        Full Description
                      </Label>
                      <Input
                        type="textarea"
                        id="description"
                        name="description"
                        placeholder="Detailed description"
                        value={formData.description}
                        onChange={handleChange}
                        rows={4}
                        className="form-control"
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <FormGroup className="mt-3">
                  <Label className="fw-semibold">Content Type</Label>
                  {id && <small className="text-muted ms-2">(Cannot be changed when editing)</small>}
                  <div className="d-flex gap-3 pt-2">
                    <div className="form-check">
                      <Input
                        type="radio"
                        name="contentMode"
                        id="modeSingle"
                        checked={contentMode === 'single'}
                        onChange={() => {
                          if (!id) {
                            setContentMode('single');
                            setModules([]);
                          }
                        }}
                        disabled={!!id}
                      />
                      <Label check for="modeSingle" className={id ? 'text-muted' : ''}>
                        Single Content (one file)
                      </Label>
                    </div>
                    <div className="form-check">
                      <Input
                        type="radio"
                        name="contentMode"
                        id="modeModules"
                        checked={contentMode === 'modules'}
                        onChange={() => {
                          if (!id) {
                            setContentMode('modules');
                            if (modules.length === 0) addModule();
                          }
                        }}
                        disabled={!!id}
                      />
                      <Label check for="modeModules" className={id ? 'text-muted' : ''}>
                        Module-wise (multiple days/modules)
                      </Label>
                    </div>
                  </div>
                </FormGroup>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <h5 className="card-title mb-4">
                  <i className="ri-folder-3-line me-2"></i>
                  Categories
                </h5>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label htmlFor="parentCategory" className="form-label fw-semibold">
                        Parent Category <span className="text-danger">*</span>
                        <span className="text-muted fw-normal ms-2 small">
                          (switch to add categories from another parent)
                        </span>
                      </Label>
                      <Input
                        type="select"
                        id="parentCategory"
                        value={selectedParentId}
                        onChange={(e) => setSelectedParentId(e.target.value)}
                        required
                        className="form-control"
                      >
                        <option value="">Select a parent category</option>
                        {parentCategories.map((parent) => (
                          <option key={parent._id} value={parent._id}>
                            {parent.name}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>

                  <Col md={6}>
                    <FormGroup>
                      <Label className="form-label fw-semibold">Selected Categories</Label>
                      <div className="d-flex flex-wrap gap-2 pt-2">
                        {formData.categories.length > 0 ? (
                          formData.categories.map((catId) => {
                            const category = getCategoryById(catId);
                            return (
                              <Badge key={catId} color="primary" className="fs-6 px-3 py-2">
                                {category?.name || catId}
                                <i
                                  className="ri-close-line ms-2 cursor-pointer"
                                  onClick={() =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      categories: prev.categories.filter((id) => id !== catId),
                                    }))
                                  }
                                  style={{ cursor: 'pointer' }}
                                ></i>
                              </Badge>
                            );
                          })
                        ) : (
                          <span className="text-muted fs-6">No categories selected</span>
                        )}
                      </div>
                    </FormGroup>
                  </Col>

                  <Col md={12}>
                    <FormGroup>
                      <Label className="form-label fw-semibold">
                        Categories
                        {selectedParentId && (
                          <span className="text-muted ms-2">
                            (assign to parent and/or subcategories)
                          </span>
                        )}
                      </Label>
                      <div
                        className="border rounded-3 p-3"
                        style={{ maxHeight: '200px', overflowY: 'auto', backgroundColor: '#f8f9fa' }}
                      >
                        {!selectedParentId ? (
                          <div className="text-center text-muted py-3">
                            <i className="ri-arrow-up-line fs-3"></i>
                            <p className="mb-0 mt-2">Please select a parent category first</p>
                          </div>
                        ) : (
                          <>
                            {(() => {
                              const selectedParent = parentCategories.find(p => p._id === selectedParentId);
                              return (
                                <>
                                  <div className="form-check mb-2 pb-2 border-bottom">
                                    <Input
                                      type="checkbox"
                                      className="form-check-input"
                                      id={`category-parent-${selectedParentId}`}
                                      value={selectedParentId}
                                      checked={formData.categories.includes(selectedParentId)}
                                      onChange={handleCategoryChange}
                                    />
                                    <Label className="form-check-label fw-medium" htmlFor={`category-parent-${selectedParentId}`}>
                                      <i className="ri-folder-line me-1 text-warning"></i>
                                      {selectedParent?.name || 'Parent'} (assign directly to this category)
                                    </Label>
                                  </div>
                                  {filteredSubcategories.length > 0 ? (
                                    filteredSubcategories.map((category) => (
                                      <div key={category._id} className="form-check mb-2">
                                        <Input
                                          type="checkbox"
                                          className="form-check-input"
                                          id={`category-${category._id}`}
                                          value={category._id}
                                          checked={formData.categories.includes(category._id)}
                                          onChange={handleCategoryChange}
                                        />
                                        <Label className="form-check-label" htmlFor={`category-${category._id}`}>
                                          <i className="ri-folder-open-line me-1 text-info"></i>
                                          {category.name}
                                        </Label>
                                      </div>
                                    ))
                                  ) : (
                                    <p className="text-muted small mb-0 mt-2">No subcategories under this parent</p>
                                  )}
                                </>
                              );
                            })()}
                          </>
                        )}
                      </div>
                    </FormGroup>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {contentMode === 'single' && (
              <Card>
                <CardBody>
                  <h5 className="card-title mb-4">
                    <i className="ri-file-music-line me-2"></i>
                    Media Files
                  </h5>
                  <SingleContentMediaCard
                    contentId={id}
                    hasTitle={Boolean(formData.title.trim())}
                    media={{
                      file,
                      thumbnail,
                      existingFileUrl,
                      existingFileType,
                      thumbnailPreview,
                      mediaStatus: singleMediaStatus,
                      mediaErrorMessage: singleMediaErrorMessage,
                      fileUploadStatus,
                      fileUploadProgress,
                      fileUploadError,
                      thumbnailUploadStatus,
                      thumbnailUploadProgress,
                      thumbnailUploadError,
                      removingTarget,
                    }}
                    onUploadMedia={handleSingleMediaUpload}
                    onRemoveMedia={handleSingleMediaRemove}
                  />
                </CardBody>
              </Card>
            )}

            {contentMode === 'modules' && (
              <Card className="mb-3">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="card-title mb-0">
                      <i className="ri-video-line me-2"></i>
                      Introduction Videos
                      <Badge color="secondary" className="ms-2 fw-normal">Optional</Badge>
                    </h5>
                    <Button color="success" outline type="button" onClick={addIntroduction}>
                      <i className="ri-add-line me-1"></i>
                      Add Introduction
                    </Button>
                  </div>
                  {id ? (
                    <Alert color="success" className="mb-4 py-2">
                      <i className="ri-upload-cloud-2-line me-2"></i>
                      Drop video or thumbnails to upload immediately. Use Remove on each tile to clear media.
                    </Alert>
                  ) : (
                    <Alert color="warning" className="mb-4 py-2">
                      <i className="ri-upload-cloud-line me-2"></i>
                      Save content once, then drop files on each introduction to upload automatically.
                    </Alert>
                  )}
                  <p className="text-muted small mb-4">
                    Optional intro videos shown before day modules. Returned in APIs under the <code>introductions</code> key.
                  </p>
                  {introductions.length === 0 ? (
                    <div className="text-center py-4 border rounded bg-light">
                      <i className="ri-video-line display-4 text-muted"></i>
                      <p className="mt-2 mb-2">No introduction videos added yet</p>
                      <Button color="primary" type="button" onClick={addIntroduction}>
                        <i className="ri-add-line me-1"></i>
                        Add First Introduction
                      </Button>
                    </div>
                  ) : (
                    introductions.map((intro, idx) => (
                      <IntroductionItemCard
                        key={idx}
                        item={intro}
                        index={idx}
                        contentId={id}
                        onUpdate={(field, value) => updateIntroduction(idx, field, value)}
                        onUploadMedia={(payload) => handleIntroductionMediaUpload(idx, payload)}
                        onRemoveMedia={(target) => handleIntroductionMediaRemove(idx, target)}
                        onRemove={() => removeIntroduction(idx)}
                        canRemove
                      />
                    ))
                  )}
                </CardBody>
              </Card>
            )}

            {contentMode === 'modules' && (
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="card-title mb-0">
                      <i className="ri-stack-line me-2"></i>
                      Modules (Days)
                    </h5>
                    <Button color="success" outline onClick={addModule}>
                      <i className="ri-add-line me-1"></i>
                      Add Module
                    </Button>
                  </div>
                  <Alert color="info" className="mb-4">
                    <h6 className="alert-heading">
                      <i className="ri-information-line me-2"></i>
                      How users unlock content
                    </h6>
                    <ul className="mb-0 ps-3 small">
                      <li><strong>Day unlock:</strong> Day 1 available at join. Day N unlocks 24 hours after Day N-1 is completed.</li>
                      <li><strong>Media modules (audio/video):</strong> Questions are locked until the user completes the audio/video for that day.</li>
                      <li><strong>Question-only modules:</strong> Questions unlock sequentially (answer Q1 → Q2 unlocks, etc.). Day 2 unlocks 24h after Day 1 is done.</li>
                    </ul>
                  </Alert>
                  {!id && (
                    <Alert color="warning" className="mb-4">
                      <i className="ri-upload-cloud-line me-2"></i>
                      Save this content once to get an ID, then media uploads start automatically when you drop files on each day.
                    </Alert>
                  )}
                  {id && (
                    <Alert color="success" className="mb-4 py-2">
                      <i className="ri-upload-cloud-2-line me-2"></i>
                      Drop audio/video or thumbnails on each day to upload immediately. Metadata syncs automatically before each upload; use Save when you are done editing titles and descriptions.
                    </Alert>
                  )}
                  <p className="text-muted small mb-4">
                    Each module can have its own audio/video file, thumbnail, reflection section, and questions.
                  </p>
                  {modules.length === 0 ? (
                    <div className="text-center py-5 border rounded bg-light">
                      <i className="ri-stack-line display-4 text-muted"></i>
                      <p className="mt-2 mb-2">No modules added yet</p>
                      <Button color="primary" onClick={addModule}>
                        <i className="ri-add-line me-1"></i>
                        Add First Module
                      </Button>
                    </div>
                  ) : (
                    modules.map((module, idx) => (
                      <ModuleItemCard
                        key={idx}
                        module={module}
                        index={idx}
                        contentId={id}
                        onUpdate={(field, value) => updateModule(idx, field, value)}
                        onUploadMedia={(payload) => handleModuleMediaUpload(idx, payload)}
                        onRemoveMedia={(target) => handleModuleMediaRemove(idx, target)}
                        onRemove={() => removeModule(idx)}
                        canRemove={modules.length > 1}
                      />
                    ))
                  )}
                </CardBody>
              </Card>
            )}

            {contentMode === 'single' && (
              <Card>
                <CardBody>
                  <h5 className="card-title mb-4">
                    <i className="ri-file-text-line me-2"></i>
                    Transcript
                  </h5>
                  <Row>
                    <Col md={12}>
                      <FormGroup>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <Label className="form-label fw-semibold mb-0">Upload transcript (.srt only)</Label>
                          {(transcriptFileName || formData.transcript?.trim() || formData.transcriptTimestamps.length > 0) && (
                            <Button
                              type="button"
                              color="link"
                              size="sm"
                              className="p-0 text-danger"
                              onClick={() => {
                                setTranscriptFileName(null);
                                setFormData((prev) => ({ ...prev, transcript: '', transcriptTimestamps: [] }));
                              }}
                              title="Remove transcript"
                            >
                              <i className="ri-delete-bin-line me-1" />
                              Remove
                            </Button>
                          )}
                        </div>
                        <div
                          {...getTranscriptProps()}
                          className="border border-2 border-dashed rounded p-3 text-center mb-3"
                          style={{ cursor: 'pointer' }}
                        >
                          <input {...getTranscriptInputProps()} />
                            {transcriptFileName ? (
                              <>
                                <i className="ri-file-text-line me-2 text-success"></i>
                                <span className="fw-semibold">{transcriptFileName}</span>
                                <span className="text-muted small d-block mt-1">
                                  {formData.transcriptTimestamps.length} timestamp(s) — drop another to replace
                                </span>
                              </>
                            ) : (formData.transcript?.trim() || formData.transcriptTimestamps.length > 0) ? (
                              <>
                                <i className="ri-file-text-line me-2 text-success"></i>
                                <span className="fw-semibold">Transcript saved</span>
                                <span className="text-muted small d-block mt-1">
                                  {formData.transcriptTimestamps.length} timestamp(s) — drop new .srt to replace
                                </span>
                              </>
                            ) : (
                              <>
                                <i className="ri-upload-cloud-line me-2"></i>
                                Drop an SRT file here or click to browse. Exported from transcription tools (e.g. Otter, Rev).
                              </>
                            )}
                        </div>
                      </FormGroup>
                    </Col>
                    <Col md={12}>
                      <FormGroup>
                        <Label htmlFor="transcript" className="form-label fw-semibold">
                          Content Transcript
                        </Label>
                        <Input
                          type="textarea"
                          id="transcript"
                          name="transcript"
                          placeholder="Paste transcript or upload .srt above"
                          value={formData.transcript}
                          onChange={handleChange}
                          rows={6}
                          className="form-control"
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            )}

            <div className="d-flex gap-2 justify-content-end mb-4">
              <Button type="button" color="light" onClick={handleCancel} disabled={formActionsDisabled}>
                <i className="ri-close-line me-1"></i>
                Cancel
              </Button>
              <Button type="submit" color="primary" disabled={formActionsDisabled}>
                {saving ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="ri-save-line me-1"></i>
                    {id ? 'Update' : 'Create'} Content
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default AddEditContentPage;
